import { getSubjectsByDiplomaAndSection, SECTION_LABELS, SECTIONS } from '@/data/subjectsCatalog';
import { supabase } from '@/lib/supabase';
import Constants from 'expo-constants';
import * as Device from 'expo-device';
import * as Haptics from 'expo-haptics';
import { LinearGradient } from 'expo-linear-gradient';
import * as Notifications from 'expo-notifications';
import { router } from 'expo-router';
import {
  Bell,
  BellRinging,
  CalendarCheck,
  CheckCircle,
  Gear,
  GenderFemale,
  GenderMale,
  GenderNonbinary,
  Handshake,
  Lock,
  LockOpen,
  Question,
  Target,
  UserCircle,
} from 'phosphor-react-native';
import { useEffect, useRef, useState } from 'react';
import { Alert, Animated, Dimensions, Image, KeyboardAvoidingView, Platform, Pressable, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import ConfettiCannon from 'react-native-confetti-cannon';
import { ActivityIndicator, Text, TextInput } from 'react-native-paper';
import Svg, { Circle } from 'react-native-svg';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

// ✅ Détection plateforme web
const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 600; // Largeur max pour le contenu onboarding

// Design System Colors - Ernest Branding
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo400: '#A5B4FC',
  indigo200: '#C7D2FE',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate800: '#1E293B',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  purple600: '#7C3AED',
  amber500: '#F59E0B',
  amber100: '#FEF3C7',
  emerald500: '#10B981',
  emerald100: '#D1FAE5',
  rose500: '#F43F5E',
  rose100: '#FFE4E6',
};

// Options de genre
type Gender = 'male' | 'female' | 'non-binary' | 'not-specified';

interface GenderOption {
  id: Gender;
  label: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}

const GENDER_OPTIONS: GenderOption[] = [
  {
    id: 'male',
    label: 'Homme',
    icon: <GenderMale size={28} color={colors.indigo600} weight="duotone" />,
    color: colors.indigo600,
    bgColor: colors.indigo100,
  },
  {
    id: 'female',
    label: 'Femme',
    icon: <GenderFemale size={28} color={colors.rose500} weight="duotone" />,
    color: colors.rose500,
    bgColor: colors.rose100,
  },
  {
    id: 'non-binary',
    label: 'Non-binaire',
    icon: <GenderNonbinary size={28} color={colors.purple600} weight="duotone" />,
    color: colors.purple600,
    bgColor: '#EDE9FE',
  },
  {
    id: 'not-specified',
    label: 'Ne pas préciser',
    icon: <Question size={28} color={colors.slate500} weight="duotone" />,
    color: colors.slate500,
    bgColor: colors.slate100,
  },
];

interface ExistingData {
  fullName: string;
  diploma: string;
  section: string;
  subjectCodes: string[];
}

interface OnboardingFlowProps {
  userId: string;
  onComplete: () => void;
  editMode?: boolean;
  existingData?: ExistingData | null;
  skipToStep?: number;
}

// Configuration des notifications
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
  }),
});

// Fonction pour obtenir le push token
async function registerForPushNotificationsAsync(): Promise<string | null> {
  // ✅ Sur web, pas de push notifications
  if (isWeb) {
    return null;
  }

  let token = null;

  if (Platform.OS === 'android') {
    await Notifications.setNotificationChannelAsync('default', {
      name: 'default',
      importance: Notifications.AndroidImportance.MAX,
      vibrationPattern: [0, 250, 250, 250],
      lightColor: '#6366F1',
    });
  }

  if (Device.isDevice) {
    const { status: existingStatus } = await Notifications.getPermissionsAsync();
    let finalStatus = existingStatus;
    
    if (existingStatus !== 'granted') {
      const { status } = await Notifications.requestPermissionsAsync();
      finalStatus = status;
    }
    
    if (finalStatus !== 'granted') {
      return null;
    }

    try {
      const projectId = Constants.expoConfig?.extra?.eas?.projectId ?? Constants.easConfig?.projectId;
      if (projectId) {
        token = (await Notifications.getExpoPushTokenAsync({ projectId })).data;
      } else {
        token = (await Notifications.getExpoPushTokenAsync()).data;
      }
    } catch (e) {
      console.error('Error getting push token:', e);
    }
  }

  return token;
}

// Constantes pour le cercle de progression
const CIRCLE_SIZE = 130;
const CIRCLE_STROKE_WIDTH = 8;
const CIRCLE_RADIUS = (CIRCLE_SIZE - CIRCLE_STROKE_WIDTH) / 2;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;

// ✅ Largeur des sections sur web vs mobile
const getSectionCardWidth = () => {
  if (isWeb) {
    return 'auto';
  }
  return (SCREEN_WIDTH - 58) / 2;
};

export default function OnboardingFlow({ 
  userId, 
  onComplete, 
  editMode = false,
  existingData = null,
  skipToStep,
}: OnboardingFlowProps) {
  // 6 étapes : 1=Prénom, 2=Genre, 3=Spécialité BTS, 4=Matières, 5=Notifications, 6=Pacte
  const getInitialStep = () => {
    if (skipToStep) return skipToStep;
    if (editMode) return 4;
    return 1;
  };

  const [step, setStep] = useState(getInitialStep());
  // ✅ Diplôme fixé à BTS (plus de choix DCG)
  const selectedDiploma = 'BTS' as const;
  const [selectedSection, setSelectedSection] = useState<string | null>(
    existingData?.section ? existingData.section : null
  );
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>(
    existingData?.subjectCodes ? existingData.subjectCodes : []
  );
  const [fullName, setFullName] = useState(
    existingData?.fullName ? existingData.fullName : ''
  );
  const [selectedGender, setSelectedGender] = useState<Gender | null>(null);
  
  // States pour notifications et pacte
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [pushToken, setPushToken] = useState<string | null>(null);
  const [pactSealed, setPactSealed] = useState(false);
  const [isPressing, setIsPressing] = useState(false);
  const [pressProgress, setPressProgress] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  
  const [isLoading, setIsLoading] = useState(false);

  // Ref pour le canon à confettis
  const confettiRef = useRef<any>(null);

  // Animations
  const progressAnim = useRef(new Animated.Value(editMode ? 100 : 0)).current;
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;
  
  // Animation pour le pacte (long press)
  const pactProgressAnim = useRef(new Animated.Value(0)).current;
  const lockScaleAnim = useRef(new Animated.Value(1)).current;
  const lockRotateAnim = useRef(new Animated.Value(0)).current;
  
  // Animation pour le serrage de main
  const handshakeScaleAnim = useRef(new Animated.Value(0)).current;
  const handshakeRotateAnim = useRef(new Animated.Value(0)).current;

  const totalSteps = editMode ? 1 : 6;

  // ✅ Messages d'Ernest OPTIMISÉS - Plus de doublons avec les sous-titres
  const ernestMessages: { [key: number]: string } = {
    1: "Avant de commencer, j'ai besoin de quelques infos pour personnaliser tes révisions. Promis, c'est rapide ! 🚀",
    2: "Cette info reste 100% confidentielle, c'est juste pour nos stats 📊",
    3: "Dis-moi ce que tu prépares et je m'adapte à ton programme !",
    4: editMode 
      ? "Modifie ta sélection comme tu veux, c'est toi le boss ! 💪"
      : "Coche les matières que tu veux bosser avec moi 💪",
    5: "Un petit rappel quotidien pour garder le rythme, ça te dit ? 🔔",
    6: `${fullName || 'Hey'}, on se fait une promesse ? Je t'aide à réussir, tu t'engages à bosser !`,
  };

  useEffect(() => {
    const targetProgress = editMode ? 100 : (step / totalSteps) * 100;
    Animated.timing(progressAnim, {
      toValue: targetProgress,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [step, editMode]);

  // Animation du long press pour le pacte
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isPressing && !pactSealed) {
      const startTime = Date.now();
      const duration = 2000; // 2 secondes
      
      interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        setPressProgress(progress);
        
        pactProgressAnim.setValue(progress);
        
        if (progress >= 1) {
          clearInterval(interval);
          setPactSealed(true);
          
          // 🎉 Vibration de succès (seulement sur mobile)
          if (!isWeb) {
            Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
          }
          
          // 🎊 Lancer les confettis
          setShowConfetti(true);
          
          // Animation de célébration du cadenas
          Animated.sequence([
            Animated.parallel([
              Animated.spring(lockScaleAnim, {
                toValue: 1.3,
                friction: 3,
                useNativeDriver: true,
              }),
              Animated.timing(lockRotateAnim, {
                toValue: 1,
                duration: 300,
                useNativeDriver: true,
              }),
            ]),
            Animated.spring(lockScaleAnim, {
              toValue: 1,
              friction: 5,
              useNativeDriver: true,
            }),
          ]).start();
          
          // 🤝 Animation du serrage de main (après un petit délai)
          setTimeout(() => {
            Animated.sequence([
              // Apparition avec zoom
              Animated.spring(handshakeScaleAnim, {
                toValue: 1.2,
                friction: 4,
                useNativeDriver: true,
              }),
              // Légère rotation gauche-droite (serrage)
              Animated.sequence([
                Animated.timing(handshakeRotateAnim, {
                  toValue: 1,
                  duration: 100,
                  useNativeDriver: true,
                }),
                Animated.timing(handshakeRotateAnim, {
                  toValue: -1,
                  duration: 100,
                  useNativeDriver: true,
                }),
                Animated.timing(handshakeRotateAnim, {
                  toValue: 0.5,
                  duration: 100,
                  useNativeDriver: true,
                }),
                Animated.timing(handshakeRotateAnim, {
                  toValue: 0,
                  duration: 100,
                  useNativeDriver: true,
                }),
              ]),
              // Retour à la taille normale
              Animated.spring(handshakeScaleAnim, {
                toValue: 1,
                friction: 5,
                useNativeDriver: true,
              }),
            ]).start();
          }, 300);
        }
      }, 16);
    } else if (!isPressing && !pactSealed) {
      setPressProgress(0);
      Animated.timing(pactProgressAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: false,
      }).start();
    }
    
    return () => clearInterval(interval);
  }, [isPressing, pactSealed]);

  const animateTransition = (direction: 'next' | 'back', callback: () => void) => {
    const toValue = direction === 'next' ? -30 : 30;

    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue,
        duration: 150,
        useNativeDriver: true,
      }),
    ]).start(() => {
      callback();
      slideAnim.setValue(direction === 'next' ? 30 : -30);

      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    });
  };

  const handleSectionSelect = (section: string) => {
    setSelectedSection(section);
    setSelectedSubjects([]);
  };

  const handleSubjectToggle = (subjectCode: string) => {
    setSelectedSubjects((prev) => {
      if (prev.includes(subjectCode)) {
        return prev.filter((s) => s !== subjectCode);
      }
      return [...prev, subjectCode];
    });
  };

  const handleSelectAll = () => {
    if (selectedSubjects.length === subjects.length) {
      setSelectedSubjects([]);
    } else {
      setSelectedSubjects(subjects.map(s => s.subject_code));
    }
  };

  // ✅ Alert adapté web/mobile
  const showAlert = (title: string, message: string, onOk?: () => void) => {
    if (isWeb) {
      window.alert(`${title}\n\n${message}`);
      onOk?.();
    } else {
      Alert.alert(title, message, [{ text: 'OK', onPress: onOk }]);
    }
  };

  // Gestion des notifications
  const handleEnableNotifications = async () => {
    // ✅ Sur web, passer directement à l'étape suivante
    if (isWeb) {
      animateTransition('next', () => setStep(6));
      return;
    }

    const token = await registerForPushNotificationsAsync();
    if (token) {
      setPushToken(token);
      setNotificationsEnabled(true);
    }
    animateTransition('next', () => setStep(6));
  };

  const handleSkipNotifications = () => {
    animateTransition('next', () => setStep(6));
  };

  // Navigation
  const handleContinueToStep2 = () => {
    if (!fullName.trim()) {
      showAlert('Prénom requis', 'Entre ton prénom pour continuer');
      return;
    }
    animateTransition('next', () => setStep(2));
  };

  const handleContinueToStep3 = () => {
    animateTransition('next', () => setStep(3));
  };

  const handleContinueToStep4 = () => {
    if (!selectedSection) {
      showAlert('Sélection requise', 'Choisis ta spécialité pour continuer');
      return;
    }
    animateTransition('next', () => setStep(4));
  };

  const handleContinueToStep5 = () => {
    if (selectedSubjects.length === 0) {
      showAlert('Sélection requise', 'Sélectionne au moins une matière pour continuer');
      return;
    }
    // ✅ Sur web, sauter l'étape notifications et aller directement au pacte
    if (isWeb) {
      animateTransition('next', () => setStep(6));
    } else {
      animateTransition('next', () => setStep(5));
    }
  };

  const handleBack = () => {
    if (editMode) {
      router.back();
      return;
    }
    
    if (step === 2) {
      animateTransition('back', () => setStep(1));
    } else if (step === 3) {
      if (skipToStep) {
        router.back();
        return;
      }
      animateTransition('back', () => setStep(2));
    } else if (step === 4) {
      animateTransition('back', () => {
        setStep(3);
        setSelectedSubjects([]);
      });
    } else if (step === 5) {
      animateTransition('back', () => setStep(4));
    } else if (step === 6) {
      // ✅ Sur web, revenir à l'étape 4 (pas de notifications)
      if (isWeb) {
        animateTransition('back', () => setStep(4));
      } else {
        animateTransition('back', () => setStep(5));
      }
    }
  };

  const handleFinish = async () => {
    if (!pactSealed && !editMode) {
      showAlert('Scelle le pacte !', 'Maintiens le bouton pour valider notre engagement');
      return;
    }

    setIsLoading(true);
    try {
      if (editMode) {
        const { error } = await supabase
          .from('profiles')
          .update({
            subject_codes: selectedSubjects,
          })
          .eq('id', userId);

        if (error) throw error;

        showAlert(
          'Matières mises à jour !',
          `${selectedSubjects.length} matière${selectedSubjects.length > 1 ? 's' : ''} sélectionnée${selectedSubjects.length > 1 ? 's' : ''}.`,
          onComplete
        );
      } else {
        const { error } = await supabase
          .from('profiles')
          .update({
            diploma: 'BTS',
            section: selectedSection,
            subject_codes: selectedSubjects,
            full_name: fullName.trim(),
            gender: selectedGender,
            push_token: pushToken,
            notifications_enabled: notificationsEnabled,
            pact_accepted: true,
            pact_accepted_at: new Date().toISOString(),
            onboarding_completed: true,
          })
          .eq('id', userId);

        if (error) throw error;

        onComplete();
      }
    } catch (error: any) {
      console.error('Onboarding error:', error);
      showAlert('Erreur', error.message || 'Une erreur est survenue. Réessaie.');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Sections BTS directement (plus de filtre par diplôme)
  const sections = SECTIONS;
  const subjects = selectedSection
    ? getSubjectsByDiplomaAndSection('BTS', selectedSection)
    : [];

  const canContinueStep3 = !!selectedSection;
  const allSelected = subjects.length > 0 && selectedSubjects.length === subjects.length;

  const ErnestMessage = ({ message }: { message: string }) => (
    <View style={styles.ernestMessage}>
      <View style={styles.ernestAvatarContainer}>
        <View style={styles.ernestAvatar}>
          <Image 
            source={require('../../assets/images/ernest.png')} 
            style={styles.ernestImage}
          />
        </View>
      </View>
      <View style={styles.ernestBubble}>
        <Text style={styles.ernestName}>Ernest</Text>
        <Text style={styles.ernestText}>{message}</Text>
      </View>
    </View>
  );

  // Calcul de la rotation du cadenas
  const lockRotation = lockRotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '15deg'],
  });

  // Calcul de la rotation du serrage de main
  const handshakeRotation = handshakeRotateAnim.interpolate({
    inputRange: [-1, 0, 1],
    outputRange: ['-15deg', '0deg', '15deg'],
  });

  // Calcul du strokeDashoffset pour le cercle de progression
  const strokeDashoffset = CIRCLE_CIRCUMFERENCE * (1 - pressProgress);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      {/* 🎊 Canon à confettis */}
      {showConfetti && (
        <ConfettiCannon
          ref={confettiRef}
          count={150}
          origin={{ x: SCREEN_WIDTH / 2, y: 0 }}
          autoStart={true}
          fadeOut={true}
          fallSpeed={3000}
          explosionSpeed={350}
          colors={[colors.indigo500, colors.purple600, colors.amber500, colors.emerald500, colors.rose500]}
        />
      )}

      {/* Progress Header */}
      <View style={styles.progressContainer}>
        {/* ✅ Wrapper avec largeur max */}
        <View style={styles.progressInner}>
          {editMode ? (
            <Text style={styles.stepText}>Modifier mes matières</Text>
          ) : skipToStep === 3 ? (
            <Text style={styles.stepText}>Changer de spécialité</Text>
          ) : (
            <Text style={styles.stepText}>Étape {step} sur {totalSteps}</Text>
          )}
          <View style={styles.progressBar}>
            <Animated.View
              style={[
                styles.progressFill,
                {
                  width: progressAnim.interpolate({
                    inputRange: [0, 100],
                    outputRange: ['0%', '100%'],
                  }),
                },
              ]}
            >
              <LinearGradient
                colors={[colors.indigo600, colors.purple600]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={StyleSheet.absoluteFill}
              />
            </Animated.View>
          </View>
          {!editMode && !skipToStep && (
            <View style={styles.dotsContainer}>
              {[1, 2, 3, 4, 5, 6].map((s) => (
                <View
                  key={s}
                  style={[
                    styles.dot,
                    step >= s && styles.dotActive,
                  ]}
                />
              ))}
            </View>
          )}
        </View>
      </View>

      {/* Content */}
      <Animated.View
        style={[
          styles.content,
          {
            opacity: fadeAnim,
            transform: [{ translateX: slideAnim }],
          },
        ]}
      >
        {/* STEP 1: Welcome + Name - ✅ TEXTES OPTIMISÉS */}
        {step === 1 && !editMode && !skipToStep && (
          <ScrollView 
            showsVerticalScrollIndicator={false} 
            contentContainerStyle={styles.scrollContent}
          >
            {/* ✅ Inner container pour largeur max */}
            <View style={styles.innerContainer}>
              <View style={styles.header}>
                <Text style={styles.headerEmoji}>👋</Text>
                <Text style={styles.headerTitle}>Salut, moi c'est Ernest !</Text>
                <Text style={styles.headerSubtitle}>
                  Ton coach IA pour cartonner tes exams
                </Text>
              </View>

              <ErnestMessage message={ernestMessages[1]} />

              <View style={styles.nameContainer}>
                <Text style={styles.nameLabel}>Et toi, c'est quoi ton prénom ?</Text>
                <TextInput
                  mode="outlined"
                  placeholder="Ex : Kylian"
                  value={fullName}
                  onChangeText={setFullName}
                  style={styles.nameInput}
                  outlineColor={colors.slate200}
                  activeOutlineColor={colors.indigo600}
                  autoFocus={!isWeb}
                />
              </View>

              <TouchableOpacity
                style={[
                  styles.primaryButton,
                  !fullName.trim() && styles.primaryButtonDisabled,
                ]}
                onPress={handleContinueToStep2}
                disabled={!fullName.trim()}
                activeOpacity={0.8}
              >
                <LinearGradient
                  colors={!fullName.trim() ? [colors.slate300, colors.slate400] : [colors.indigo600, colors.purple600]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.primaryButtonGradient}
                >
                  <Text style={styles.primaryButtonText}>C'est parti !</Text>
                </LinearGradient>
              </TouchableOpacity>
            </View>
          </ScrollView>
        )}

        {/* STEP 2: Gender Selection - ✅ TEXTES OPTIMISÉS */}
        {step === 2 && !editMode && !skipToStep && (
          <ScrollView 
            showsVerticalScrollIndicator={false} 
            contentContainerStyle={styles.scrollContent}
          >
            <View style={styles.innerContainer}>
              <View style={styles.header}>
                <View style={styles.headerIconContainer}>
                  <UserCircle size={56} color={colors.indigo600} weight="duotone" />
                </View>
                <Text style={styles.headerTitle}>Et toi, tu es...</Text>
              </View>

              <ErnestMessage message={ernestMessages[2]} />

              <View style={styles.genderContainer}>
                {GENDER_OPTIONS.map((option) => {
                  const isSelected = selectedGender === option.id;
                  return (
                    <TouchableOpacity
                      key={option.id}
                      style={[
                        styles.genderCard,
                        isSelected && [styles.genderCardSelected, { borderColor: option.color }],
                      ]}
                      onPress={() => setSelectedGender(option.id)}
                      activeOpacity={0.7}
                    >
                      <View style={[
                        styles.genderIconCircle,
                        { backgroundColor: option.bgColor },
                      ]}>
                        {option.icon}
                      </View>
                      <Text style={[
                        styles.genderLabel,
                        isSelected && { color: option.color, fontWeight: '700' },
                      ]}>
                        {option.label}
                      </Text>
                      <View style={[
                        styles.radioCircle,
                        isSelected && { borderColor: option.color },
                      ]}>
                        {isSelected && (
                          <View style={[styles.radioInner, { backgroundColor: option.color }]} />
                        )}
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </View>

              <TouchableOpacity
                style={styles.primaryButton}
                onPress={handleContinueToStep3}
                activeOpacity={0.8}
              >
                <LinearGradient
                  colors={[colors.indigo600, colors.purple600]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.primaryButtonGradient}
                >
                  <Text style={styles.primaryButtonText}>
                    {selectedGender ? 'Continuer' : 'Passer cette étape'}
                  </Text>
                </LinearGradient>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.backButton}
                onPress={handleBack}
                activeOpacity={0.7}
              >
                <Text style={styles.backButtonText}>← Revenir</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        )}

        {/* STEP 3: Spécialité BTS - ✅ SIMPLIFIÉ (plus de choix diplôme) */}
        {step === 3 && !editMode && (
          <ScrollView 
            showsVerticalScrollIndicator={false} 
            contentContainerStyle={styles.scrollContent}
          >
            <View style={styles.innerContainer}>
              <View style={styles.header}>
                <Text style={styles.headerEmoji}>🎓</Text>
                <Text style={styles.headerTitle}>Ta spécialité BTS</Text>
              </View>

              <ErnestMessage message={ernestMessages[3]} />

              <View style={styles.sectionContainer}>
                <Text style={styles.sectionLabel}>Quelle spécialité ?</Text>
                <View style={styles.sectionsGrid}>
                  {sections.map((section) => (
                    <TouchableOpacity
                      key={section}
                      style={[
                        styles.sectionCard,
                        selectedSection === section && styles.sectionCardSelected,
                      ]}
                      onPress={() => handleSectionSelect(section)}
                      activeOpacity={0.7}
                    >
                      <Text style={[
                        styles.sectionTitle,
                        selectedSection === section && styles.sectionTitleSelected,
                      ]}>{section}</Text>
                      <Text style={styles.sectionSubtitle} numberOfLines={1}>
                        {SECTION_LABELS[section]}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              <TouchableOpacity
                style={[
                  styles.primaryButton,
                  !canContinueStep3 && styles.primaryButtonDisabled,
                ]}
                onPress={handleContinueToStep4}
                disabled={!canContinueStep3}
                activeOpacity={0.8}
              >
                <LinearGradient
                  colors={!canContinueStep3 ? [colors.slate300, colors.slate400] : [colors.indigo600, colors.purple600]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.primaryButtonGradient}
                >
                  <Text style={styles.primaryButtonText}>Continuer</Text>
                </LinearGradient>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.backButton}
                onPress={handleBack}
                activeOpacity={0.7}
              >
                <Text style={styles.backButtonText}>← Revenir</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        )}

        {/* STEP 4: Subjects - ✅ TEXTES OPTIMISÉS */}
        {step === 4 && (
          <ScrollView 
            showsVerticalScrollIndicator={false} 
            contentContainerStyle={styles.scrollContent}
          >
            <View style={styles.innerContainer}>
              <View style={styles.header}>
                <Text style={styles.headerEmoji}>📚</Text>
                <Text style={styles.headerTitle}>Tes matières</Text>
                <View style={styles.diplomaBadge}>
                  <Text style={styles.diplomaBadgeText}>BTS • {selectedSection}</Text>
                </View>
              </View>

              <ErnestMessage message={ernestMessages[4]} />

              {subjects.length > 0 && (
                <TouchableOpacity
                  style={[
                    styles.selectAllButton,
                    allSelected && styles.selectAllButtonActive,
                  ]}
                  onPress={handleSelectAll}
                  activeOpacity={0.7}
                >
                  <CheckCircle 
                    size={20} 
                    color={allSelected ? colors.white : colors.indigo600} 
                    weight={allSelected ? "fill" : "bold"} 
                  />
                  <Text style={[
                    styles.selectAllText,
                    allSelected && styles.selectAllTextActive,
                  ]}>
                    {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                  </Text>
                </TouchableOpacity>
              )}

              <View style={styles.subjectsContainer}>
                {subjects.map((subject) => {
                  const isSelected = selectedSubjects.includes(subject.subject_code);

                  return (
                    <TouchableOpacity
                      key={subject.subject_code}
                      style={[
                        styles.subjectCard,
                        isSelected && styles.subjectCardSelected,
                      ]}
                      onPress={() => handleSubjectToggle(subject.subject_code)}
                      activeOpacity={0.7}
                    >
                      <View style={[
                        styles.checkbox,
                        isSelected && styles.checkboxSelected,
                      ]}>
                        {isSelected && <Text style={styles.checkmark}>✓</Text>}
                      </View>
                      <Text style={[
                        styles.subjectText,
                        isSelected && styles.subjectTextSelected,
                      ]}>{subject.subject}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>

              <View style={styles.counterContainer}>
                <Text style={[
                  styles.counterText,
                  selectedSubjects.length > 0 && styles.counterTextActive,
                ]}>
                  {selectedSubjects.length === 0
                    ? 'Aucune matière sélectionnée'
                    : `${selectedSubjects.length} matière${selectedSubjects.length > 1 ? 's' : ''} sélectionnée${selectedSubjects.length > 1 ? 's' : ''}`
                  }
                </Text>
                <Text style={styles.counterHint}>
                  Tu pourras modifier ça plus tard dans ton profil
                </Text>
              </View>

              <TouchableOpacity
                style={[
                  styles.primaryButton,
                  (selectedSubjects.length === 0 || isLoading) && styles.primaryButtonDisabled,
                ]}
                onPress={editMode ? handleFinish : handleContinueToStep5}
                disabled={selectedSubjects.length === 0 || isLoading}
                activeOpacity={0.8}
              >
                <LinearGradient
                  colors={(selectedSubjects.length === 0 || isLoading) ? [colors.slate300, colors.slate400] : [colors.indigo600, colors.purple600]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.primaryButtonGradient}
                >
                  {isLoading ? (
                    <ActivityIndicator size="small" color={colors.white} />
                  ) : (
                    <Text style={styles.primaryButtonText}>
                      {editMode ? 'Enregistrer' : 'Continuer'}
                    </Text>
                  )}
                </LinearGradient>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.backButton}
                onPress={handleBack}
                disabled={isLoading}
                activeOpacity={0.7}
              >
                <Text style={styles.backButtonText}>
                  {editMode ? '← Annuler' : '← Revenir'}
                </Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        )}

        {/* STEP 5: Notifications (mobile only) - ✅ TEXTES OPTIMISÉS */}
        {step === 5 && !editMode && !isWeb && (
          <ScrollView 
            showsVerticalScrollIndicator={false} 
            contentContainerStyle={styles.scrollContent}
          >
            <View style={styles.innerContainer}>
              <View style={styles.header}>
                <View style={styles.headerIconContainer}>
                  <BellRinging size={56} color={colors.indigo600} weight="duotone" />
                </View>
                <Text style={styles.headerTitle}>Un petit rappel ?</Text>
              </View>

              <ErnestMessage message={ernestMessages[5]} />

              <View style={styles.notificationCard}>
                <View style={styles.notificationBenefits}>
                  <View style={styles.benefitItem}>
                    <View style={styles.benefitIconContainer}>
                      <CalendarCheck size={22} color={colors.indigo600} weight="duotone" />
                    </View>
                    <Text style={styles.benefitText}>1 rappel par jour maximum</Text>
                  </View>
                  <View style={styles.benefitItem}>
                    <View style={styles.benefitIconContainer}>
                      <Target size={22} color={colors.indigo600} weight="duotone" />
                    </View>
                    <Text style={styles.benefitText}>Atteins tes objectifs</Text>
                  </View>
                  <View style={styles.benefitItem}>
                    <View style={styles.benefitIconContainer}>
                      <Gear size={22} color={colors.indigo600} weight="duotone" />
                    </View>
                    <Text style={styles.benefitText}>Désactivable à tout moment</Text>
                  </View>
                </View>
              </View>

              <TouchableOpacity
                style={styles.primaryButton}
                onPress={handleEnableNotifications}
                activeOpacity={0.8}
              >
                <LinearGradient
                  colors={[colors.indigo600, colors.purple600]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.primaryButtonGradient}
                >
                  <Bell size={20} color={colors.white} weight="bold" />
                  <Text style={[styles.primaryButtonText, { marginLeft: 8 }]}>Activer les rappels</Text>
                </LinearGradient>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.skipButton}
                onPress={handleSkipNotifications}
                activeOpacity={0.7}
              >
                <Text style={styles.skipButtonText}>Plus tard</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.backButton}
                onPress={handleBack}
                activeOpacity={0.7}
              >
                <Text style={styles.backButtonText}>← Revenir</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        )}

        {/* STEP 6: Pacte */}
        {step === 6 && !editMode && (
          <ScrollView 
            showsVerticalScrollIndicator={false} 
            contentContainerStyle={styles.scrollContent}
          >
            <View style={styles.innerContainer}>
              <View style={styles.pactHeader}>
                {/* Mascotte Ernest */}
                <View style={styles.pactErnestContainer}>
                  <Image 
                    source={require('../../assets/images/ernest.png')} 
                    style={styles.pactErnestImage}
                  />
                </View>

                {/* Bulles de dialogue */}
                <View style={styles.pactBubbles}>
                  <View style={styles.pactBubble}>
                    <Text style={styles.pactBubbleText}>
                      {fullName || 'Hey'}, on fait un <Text style={styles.pactBubbleBold}>PACTE</Text> ensemble ?
                    </Text>
                  </View>
                  <View style={styles.pactBubble}>
                    <Text style={styles.pactBubbleText}>
                      Je t'aide à progresser, tu donnes le meilleur de toi-même. Deal ? 🤝
                    </Text>
                  </View>
                </View>
              </View>

              {/* Bouton Long Press avec cercle de progression */}
              <View style={styles.pactButtonContainer}>
                <Text style={styles.pactInstruction}>
                  {pactSealed ? 'Pacte scellé !' : 'Maintiens pour sceller le pacte'}
                </Text>
                
                <Pressable
                  onPressIn={() => !pactSealed && setIsPressing(true)}
                  onPressOut={() => setIsPressing(false)}
                  // @ts-ignore - web only props
                  onContextMenu={(e: any) => e.preventDefault()}
                  style={[
                    styles.pactButtonWrapper,
                    isWeb && { userSelect: 'none', WebkitUserSelect: 'none', cursor: 'pointer' },
                  ]}
                >
                  <View style={styles.pactButtonOuter}>
                    {/* Cercle de progression SVG */}
                    <Svg
                      width={CIRCLE_SIZE}
                      height={CIRCLE_SIZE}
                      style={styles.pactProgressSvg}
                    >
                      {/* Cercle de fond (gris) */}
                      <Circle
                        cx={CIRCLE_SIZE / 2}
                        cy={CIRCLE_SIZE / 2}
                        r={CIRCLE_RADIUS}
                        stroke={colors.indigo100}
                        strokeWidth={CIRCLE_STROKE_WIDTH}
                        fill="transparent"
                      />
                      {/* Cercle de progression (coloré) */}
                      <Circle
                        cx={CIRCLE_SIZE / 2}
                        cy={CIRCLE_SIZE / 2}
                        r={CIRCLE_RADIUS}
                        stroke={pactSealed ? colors.emerald500 : colors.indigo500}
                        strokeWidth={CIRCLE_STROKE_WIDTH}
                        fill="transparent"
                        strokeLinecap="round"
                        strokeDasharray={CIRCLE_CIRCUMFERENCE}
                        strokeDashoffset={pactSealed ? 0 : strokeDashoffset}
                        rotation={-90}
                        origin={`${CIRCLE_SIZE / 2}, ${CIRCLE_SIZE / 2}`}
                      />
                    </Svg>
                    
                    {/* Lock icon */}
                    <Animated.View
                      style={[
                        styles.pactLockContainer,
                        {
                          transform: [
                            { scale: lockScaleAnim },
                            { rotate: lockRotation },
                          ],
                        },
                      ]}
                    >
                      <LinearGradient
                        colors={pactSealed 
                          ? [colors.emerald500, '#059669'] 
                          : [colors.indigo500, colors.purple600]
                        }
                        style={styles.pactLockGradient}
                      >
                        {pactSealed ? (
                          <Lock size={36} color={colors.white} weight="fill" />
                        ) : (
                          <LockOpen size={36} color={colors.white} weight="duotone" />
                        )}
                      </LinearGradient>
                    </Animated.View>
                  </View>
                </Pressable>
                
                {/* 🤝 Badge avec animation serrage de main */}
                {pactSealed && (
                  <Animated.View 
                    style={[
                      styles.pactSealedBadge,
                      {
                        transform: [
                          { scale: handshakeScaleAnim },
                          { rotate: handshakeRotation },
                        ],
                      },
                    ]}
                  >
                    <Handshake size={22} color={colors.emerald500} weight="fill" />
                    <Text style={styles.pactSealedText}>Engagement validé</Text>
                  </Animated.View>
                )}
              </View>

              {/* Bouton final */}
              {pactSealed && (
                <TouchableOpacity
                  style={styles.primaryButton}
                  onPress={handleFinish}
                  disabled={isLoading}
                  activeOpacity={0.8}
                >
                  <LinearGradient
                    colors={isLoading ? [colors.slate300, colors.slate400] : [colors.indigo600, colors.purple600]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={styles.primaryButtonGradient}
                  >
                    {isLoading ? (
                      <ActivityIndicator size="small" color={colors.white} />
                    ) : (
                      <Text style={styles.primaryButtonText}>Commencer à réviser 🚀</Text>
                    )}
                  </LinearGradient>
                </TouchableOpacity>
              )}

              <TouchableOpacity
                style={styles.backButton}
                onPress={handleBack}
                disabled={isLoading}
                activeOpacity={0.7}
              >
                <Text style={styles.backButtonText}>← Revenir</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        )}
      </Animated.View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.slate50,
  },
  progressContainer: {
    paddingTop: isWeb ? 24 : 60,
    paddingHorizontal: 24,
    paddingBottom: 16,
    backgroundColor: colors.white,
    borderBottomWidth: 1,
    borderBottomColor: colors.slate100,
    // ✅ Centrage sur web
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  // ✅ Inner container pour le header de progression
  progressInner: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
  },
  stepText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.slate500,
    textAlign: 'center',
    marginBottom: 12,
  },
  progressBar: {
    height: 6,
    backgroundColor: colors.slate200,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    overflow: 'hidden',
  },
  dotsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
    marginTop: 12,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.slate200,
  },
  dotActive: {
    backgroundColor: colors.indigo600,
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingBottom: 40,
    // ✅ Centrage sur web
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  // ✅ Inner container pour le contenu
  innerContainer: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
  },
  header: {
    alignItems: 'center',
    marginBottom: 24,
  },
  headerIconContainer: {
    marginBottom: 16,
  },
  headerEmoji: {
    fontSize: 56,
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: 26,
    fontWeight: '700',
    color: colors.slate900,
    textAlign: 'center',
    marginBottom: 8,
  },
  headerSubtitle: {
    fontSize: 16,
    color: colors.slate500,
    textAlign: 'center',
    lineHeight: 24,
  },
  ernestMessage: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: colors.indigo50,
    borderWidth: 1,
    borderColor: colors.indigo200,
    borderRadius: 16,
    padding: 14,
    marginBottom: 24,
    gap: 12,
  },
  ernestAvatarContainer: {
    shadowColor: colors.indigo600,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
  },
  ernestAvatar: {
    width: 48,
    height: 48,
    borderRadius: 14,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.indigo200,
  },
  ernestImage: {
    width: 36,
    height: 36,
    resizeMode: 'contain',
  },
  ernestBubble: {
    flex: 1,
  },
  ernestName: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.indigo600,
    marginBottom: 4,
  },
  ernestText: {
    fontSize: 14,
    color: colors.slate700,
    lineHeight: 20,
  },
  diplomaBadge: {
    backgroundColor: colors.indigo100,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    marginTop: 8,
  },
  diplomaBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.indigo600,
  },
  optionsContainer: {
    gap: 12,
    marginBottom: 24,
  },
  optionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 16,
    borderWidth: 2,
    borderColor: colors.slate200,
    // ✅ Cursor pointer sur web
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  optionCardSelected: {
    borderColor: colors.indigo500,
    backgroundColor: colors.indigo50,
  },
  optionIconCircle: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: colors.slate100,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  optionIconCircleSelected: {
    backgroundColor: colors.indigo100,
  },
  optionEmoji: {
    fontSize: 24,
  },
  optionInfo: {
    flex: 1,
  },
  optionTitle: {
    fontSize: 17,
    fontWeight: '600',
    color: colors.slate900,
    marginBottom: 4,
  },
  optionDescription: {
    fontSize: 13,
    color: colors.slate500,
  },
  radioCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: colors.slate300,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioCircleSelected: {
    borderColor: colors.indigo600,
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: colors.indigo600,
  },
  sectionContainer: {
    marginBottom: 24,
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.slate500,
    marginBottom: 12,
  },
  sectionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  sectionCard: {
    // ✅ Largeur adaptée web/mobile
    width: isWeb ? 'auto' : (SCREEN_WIDTH - 58) / 2,
    minWidth: isWeb ? 140 : undefined,
    flex: isWeb ? 1 : undefined,
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 14,
    borderWidth: 2,
    borderColor: colors.slate200,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  sectionCardSelected: {
    borderColor: colors.indigo500,
    backgroundColor: colors.indigo50,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate900,
    marginBottom: 4,
  },
  sectionTitleSelected: {
    color: colors.indigo700,
  },
  sectionSubtitle: {
    fontSize: 11,
    color: colors.slate400,
  },
  nameContainer: {
    marginBottom: 32,
  },
  nameLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.slate900,
    marginBottom: 10,
  },
  nameInput: {
    backgroundColor: colors.white,
    fontSize: 16,
  },
  nameHint: {
    fontSize: 13,
    color: colors.slate400,
    marginTop: 10,
    textAlign: 'center',
  },
  primaryButton: {
    borderRadius: 14,
    overflow: 'hidden',
    shadowColor: colors.indigo600,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 4,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  primaryButtonDisabled: {
    shadowOpacity: 0,
    elevation: 0,
  },
  primaryButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 18,
  },
  primaryButtonText: {
    color: colors.white,
    fontSize: 17,
    fontWeight: '700',
    textAlign: 'center',
  },
  selectAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    backgroundColor: colors.indigo50,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: colors.indigo200,
    marginBottom: 16,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  selectAllButtonActive: {
    backgroundColor: colors.indigo600,
    borderColor: colors.indigo600,
  },
  selectAllText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.indigo600,
  },
  selectAllTextActive: {
    color: colors.white,
  },
  subjectsContainer: {
    gap: 10,
    marginBottom: 20,
  },
  subjectCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 14,
    borderWidth: 2,
    borderColor: colors.slate200,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  subjectCardSelected: {
    borderColor: colors.indigo500,
    backgroundColor: colors.indigo50,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: colors.slate300,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  checkboxSelected: {
    backgroundColor: colors.indigo600,
    borderColor: colors.indigo600,
  },
  checkmark: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '700',
  },
  subjectText: {
    flex: 1,
    fontSize: 15,
    color: colors.slate900,
  },
  subjectTextSelected: {
    fontWeight: '600',
  },
  counterContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  counterText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.slate400,
  },
  counterTextActive: {
    color: colors.indigo600,
  },
  counterHint: {
    fontSize: 12,
    color: colors.slate400,
    marginTop: 4,
  },
  backButton: {
    alignItems: 'center',
    padding: 16,
    marginTop: 8,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  backButtonText: {
    fontSize: 15,
    color: colors.slate500,
    fontWeight: '600',
  },
  
  // Styles pour l'étape Genre
  genderContainer: {
    gap: 12,
    marginBottom: 16,
  },
  genderCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 16,
    borderWidth: 2,
    borderColor: colors.slate200,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  genderCardSelected: {
    backgroundColor: colors.slate50,
  },
  genderIconCircle: {
    width: 52,
    height: 52,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  genderLabel: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate900,
  },
  genderHint: {
    fontSize: 13,
    color: colors.slate400,
    textAlign: 'center',
    marginBottom: 24,
  },

  // Styles pour l'étape Notifications (Step 5)
  notificationCard: {
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 24,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: colors.slate200,
  },
  notificationDescription: {
    fontSize: 15,
    color: colors.slate600,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 24,
  },
  notificationBenefits: {
    gap: 12,
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: colors.slate50,
    padding: 14,
    borderRadius: 12,
  },
  benefitIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: colors.indigo100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  benefitText: {
    flex: 1,
    fontSize: 14,
    color: colors.slate700,
    fontWeight: '500',
  },
  skipButton: {
    alignItems: 'center',
    padding: 16,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  skipButtonText: {
    fontSize: 15,
    color: colors.slate500,
    fontWeight: '600',
  },

  // Styles pour l'étape Pacte (Step 6)
  pactHeader: {
    alignItems: 'center',
    marginBottom: 32,
  },
  pactErnestContainer: {
    marginBottom: 20,
  },
  pactErnestImage: {
    width: 100,
    height: 100,
    resizeMode: 'contain',
  },
  pactBubbles: {
    width: '100%',
    gap: 12,
  },
  pactBubble: {
    backgroundColor: colors.indigo100,
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.indigo200,
  },
  pactBubbleText: {
    fontSize: 16,
    color: colors.slate800,
    lineHeight: 24,
    textAlign: 'center',
  },
  pactBubbleBold: {
    fontWeight: '800',
    color: colors.indigo700,
  },
  pactButtonContainer: {
    alignItems: 'center',
    marginBottom: 32,
  },
  pactInstruction: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate700,
    marginBottom: 20,
  },
  pactButtonWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  pactButtonOuter: {
    width: CIRCLE_SIZE,
    height: CIRCLE_SIZE,
    borderRadius: CIRCLE_SIZE / 2,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: colors.indigo600,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 8,
  },
  pactProgressSvg: {
    position: 'absolute',
  },
  pactLockContainer: {
    zIndex: 1,
  },
  pactLockGradient: {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pactSealedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 20,
    backgroundColor: colors.emerald100,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
  },
  pactSealedText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.emerald500,
  },
});
